import { useState, useCallback, useRef } from 'react';
import { AutocompletePatientSuccessResponse, LGPDDeniedReason, PatientService } from 'src/services/patient';
import { onlyDigits } from 'src/helpers/string';
import { validateCPF } from 'src/helpers/cpf';

interface UsePatientAutocompleteOptions {
  prescriberId: string | null;
  onSuccess?: (data: AutocompletePatientSuccessResponse) => void;
  onNotFound?: () => void;
  onLGPDDenied?: (reason: LGPDDeniedReason) => void;
}

interface UsePatientAutocompleteReturn {
  loading: boolean;
  autocompleteData: AutocompletePatientSuccessResponse | null;
  error: string | null;
  fetchPatientByCPF: (cpf: string, birthDate?: string) => Promise<void>;
  clearAutocompleteData: () => void;
  isApiUnavailable: boolean;
}

export function usePatientAutocomplete(options: UsePatientAutocompleteOptions): UsePatientAutocompleteReturn {
  const { prescriberId, onSuccess, onNotFound, onLGPDDenied } = options;

  const [loading, setLoading] = useState(false);
  const [autocompleteData, setAutocompleteData] = useState<AutocompletePatientSuccessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isApiUnavailable, setIsApiUnavailable] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchPatientByCPF = useCallback(
    async (cpf: string, birthDate?: string) => {
      if (!prescriberId) return;

      const cleanCPF = onlyDigits(cpf);

      if (!validateCPF(cleanCPF)) {
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setLoading(true);
      setError(null);
      setIsApiUnavailable(false);

      try {
        const data = await PatientService.autocompletePatientByCPF(prescriberId, cleanCPF, birthDate);

        if (data.status === 'denied') {
          if (onLGPDDenied) {
            onLGPDDenied(data.reason);
          }
          return;
        }

        if (data.status === 'success') {
          setAutocompleteData(data);

          if (onSuccess) {
            onSuccess(data);
          }
        }
      } catch (err: any) {
        if (err?.response?.status === 503) {
          setIsApiUnavailable(true);
        }

        if (onNotFound) {
          onNotFound();
        }
      } finally {
        setLoading(false);
      }
    },
    [prescriberId, onSuccess, onNotFound, onLGPDDenied]
  );

  const clearAutocompleteData = useCallback(() => {
    setAutocompleteData(null);
    setError(null);
    setIsApiUnavailable(false);
  }, []);

  return {
    loading,
    autocompleteData,
    error,
    fetchPatientByCPF,
    clearAutocompleteData,
    isApiUnavailable,
  };
}
